import { EventData } from './models/EventData';
import { MessagePayload, MessageType } from './messages';

type Listener<P> = (payload: P) => void;
type UnknownJSON = { key?: string } | null;

/**
 * Allows sending messages filtered by the `key` passed in the constructor.
 *
 * The messages are send using the underlying postMessage API, the target window
 * of the outbound messages can be set by passing a `Window` object in the constructor.
 * By default, the outbound window is set to `window.parent`.
 *
 * The window object where to attach the `message` event listener to listen to events
 * received can be set by passing a third argument (`inboundWindow`). Defaults to `window`.
 */
export class InteractiveMessenger {
	private listeners: Record<string, Array<Listener<any>> | undefined>;

	private key: string;

	/**
	 * The window object that will receive the messages emitted
	 */
	private outboundWindow;

	/**
	 * The window object that will listen to the messages received
	 */
	private inboundWindow;

	/**
	 * @param key The key that will be used to filter the messages out
	 * @param outboundWindow The window object that will receive the messages emitted
	 * @param inboundWindow The window object that will listen to the messages received
	 */
	constructor(key: string, outboundWindow?: Window, inboundWindow?: Window) {
		this.key = key;
		this.listeners = {};
		this.outboundWindow = outboundWindow || window.parent;
		this.inboundWindow = inboundWindow || window;
		this.init();
	}

	/**
	 * Attaches a listener to a given `eventName`.
	 */
	on<T extends MessageType>(type: T, listener: Listener<MessagePayload[T]>): void {
		const listeners = this.listeners[type];

		if (!listeners) {
			this.listeners[type] = [listener];
			return;
		}

		listeners.push(listener);
	}

	/**
	 * Emits the event identified by `eventName`.
	 */
	emit<T extends MessageType>(type: T, payload: MessagePayload[T]): void {
		const data = JSON.stringify({
			key: this.key,
			message: { type, payload },
		});

		this.outboundWindow.postMessage(data, '*');
	}

	/**
	 * Removes the given `listener` for the `eventName` provided.
	 * If no `listener` given, then this function clears all listeners
	 * for `eventName`.
	 */
	off<T extends MessageType>(type: T, listener?: Listener<MessagePayload[T]>): void {
		const events = this.listeners[type];
		const liveEvents: Listener<MessagePayload[T]>[] = [];

		if (events && listener) {
			for (let i = 0, len = events.length; i < len; i += 1) {
				if (events[i] !== listener) {
					liveEvents.push(events[i]);
				}
			}
		}
		if (liveEvents.length) {
			this.listeners[type] = liveEvents;
		} else {
			delete this.listeners[type];
		}
	}

	/**
	 * Removes all listeners and stops listening for `message` events.
	 */
	destroy(): void {
		this.listeners = {};
		this.inboundWindow.removeEventListener('message', this.onMessage);
	}

	/**
	 * Sets the outbound window object that will receive
	 * the messages emitted by this Messenger instance.
	 */
	setOutboundWindow(win: Window): void {
		this.outboundWindow = win;
	}

	protected init(): void {
		this.inboundWindow.addEventListener('message', this.onMessage);
	}

	/**
	 * @param event
	 * @throws {Error}  Error while parsing `event.data` as Json
	 * @returns
	 */
	protected onMessage = ({ data }: MessageEvent): void => {
		if (typeof data !== 'string') {
			return;
		}

		let parsedData: UnknownJSON;
		try {
			parsedData = JSON.parse(data) as UnknownJSON;
		} catch (error) {
			// will receive a message with a non JSON string we ignore the message
			return;
		}

		if (!parsedData || !parsedData.key) {
			return;
		}

		const eventData = parsedData as EventData;

		const eKey = eventData.key;
		if (eKey !== this.key) {
			// If key is not the same as the one registered for this event
			// emitter, we ignore it.
			return;
		}

		const { type: action, payload } = eventData.message;

		const listeners = this.listeners[action];
		if (listeners) {
			listeners.forEach((c) => {
				c(payload);
			});
		}
	};
}
